import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import {
  ClaimStatus,
  IClaim,
  IPagedData,
  IPersistedMyClaimsFilters,
  IRetailerStore,
  IRetailerStores,
  IStoreState,
} from '../../types';
import { FullPageLayout } from '../../layouts';
import { Pagination } from 'react-bootstrap';
import { Button, Card, Checkbox, Dropdown, StyledCalendar } from '@orderly/morrisons-component-library';
import Table from '../../components/Table/Table';
import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../constants';
import { persistMyClaimsFiltersAction } from '../../actions/MyClaims';
import { EmptyRecord, SearchBar, SkeletonLoader } from '../../components';
import { getMyClaimsPage } from '../../api/MyClaims';
import { formatShortDateString } from '../../helpers/Format';
import styles from './MyClaims.module.scss';
import { addSpaceBetweenWords, mapEnumToOptions } from '../../helpers/EnumHelper';
import { navigateTo } from '../../routing/Navigation';

interface IMyClaimsProps {
  userStores: IRetailerStores;
  persistedMyClaimsListFilters: IPersistedMyClaimsFilters;
  persistMyClaimsFilters: (filters: IPersistedMyClaimsFilters) => void;
}

const MyClaims = ({
  userStores,
  persistedMyClaimsListFilters,
  persistMyClaimsFilters,
}: Readonly<IMyClaimsProps>) => {
  const formatter = new Intl.DateTimeFormat('en-GB');
  const { currentPage, selectedDate, selectedStore, currentFilter, search } = persistedMyClaimsListFilters;
  const [totalPages, setTotalPages] = useState<number>(1);
  const searchDate = formatter.format(selectedDate);
  const itemsPerPage = 25;

  const { data: claimsPage, isLoading } = useQuery<IPagedData<IClaim>, Error>(
    [QUERY_KEYS.MY_CLAIMS, selectedStore, currentPage, searchDate, currentFilter, search],
    () => getMyClaimsPage({
      search,
      currentPage,
      currentFilter,
      dateFilter: searchDate,
      storeId: selectedStore,
      pageSize: itemsPerPage,
    }),
    {
      enabled: selectedStore !== null,
    },
  );

  const setSelectedStore = useCallback(
    (storeId: number) => {
      persistMyClaimsFilters({
        search,
        currentFilter,
        selectedDate,
        currentPage,
        selectedStore: storeId,
      });
    },
    [persistMyClaimsFilters, selectedDate, currentPage, currentFilter, search],
  );

  useEffect(
    () => {
      if (selectedStore != null) {
        return;
      }

      if (userStores && userStores.stores.length > 0) {
        setSelectedStore(userStores.stores[0].externalId);
      }
    },
    [userStores, selectedStore, setSelectedStore],
  );

  const claims = useMemo(
    () => {
      if (claimsPage && claimsPage.data) {
        return claimsPage.data;
      }

      return [];
    },
    [claimsPage],
  );

  useEffect(
    () => {
      if (claimsPage) {
        const total = Math.ceil(claimsPage.totalData / itemsPerPage);
        setTotalPages(total);
      }
    },
    [claimsPage, selectedStore, currentPage, currentFilter],
  );

  const selectStore = (externalStoreId: number) => {
    setCurrentPage(1);
    setSelectedStore(externalStoreId);
  };

  const setCurrentPage = (newPage: number) => {
    persistMyClaimsFilters({
      search,
      currentFilter,
      selectedStore,
      selectedDate,
      currentPage: newPage,
    });
  };

  const onCalendarChange = (newDate: Date) => {
    persistMyClaimsFilters({
      search,
      currentFilter,
      selectedStore,
      selectedDate: newDate,
      currentPage: 1,
    });
  };

  const onFilterChange = (value: number, checked: boolean) => {
    persistMyClaimsFilters({
      search,
      selectedStore,
      selectedDate,
      currentFilter: checked ? value : -1,
      currentPage: 1,
    });
  };

  const onSearchChange = (value: string) => {
    persistMyClaimsFilters({
      selectedStore,
      selectedDate,
      currentFilter,
      search: value,
      currentPage: 1,
    });
  };

  return (
    <FullPageLayout
      heading="My Claims"
      breadcrumbs={[
        { key: 0, text: 'Orders', url: '/orders' },
        { key: 1, text: 'My Claims', url: '/orders/my-claims' },
      ]}
      headerAdditionalContent={
        (userStores != null && userStores.stores != null && userStores.stores.length > 1) &&
        <Dropdown
          className={styles.storeSelect}
          label=""
          name="store-sort"
          onChange={value => selectStore(value.value as number)}
          selectedValue={selectedStore}
          options={userStores.stores.map((store: IRetailerStore) => {
            return {
              value: store.externalId,
              label: `${store.name} [${store.externalId}]`,
            };
          })}
        />
      }
    >
      <div className={styles.container}>
        <div className={styles.filters}>
          <Card className={styles.card}>
            <div>
              <SearchBar
                label="Search"
                value={search}
                onChange={val => onSearchChange(val)}
              />
            </div>
            <div>
              <span>Status</span>
              <div>
                {
                  mapEnumToOptions(ClaimStatus)
                    .sort((a, b) => a.label.localeCompare(b.label))
                    .map((status) => {
                      return (
                        <Checkbox
                          key={status.value}
                          checked={currentFilter === status.value}
                          label={addSpaceBetweenWords(status.label.toString())}
                          name={status.value.toString()}
                          onChange={(_, checked) => onFilterChange(status.value, checked)}
                          isSmall={true}
                          className=""
                          disabled={false}
                        />
                      );
                    })
                }
              </div>
            </div>
            <hr />
            <div>
              <span>Date</span>
              <StyledCalendar
                onChange={newDate => onCalendarChange(newDate as Date)}
                value={selectedDate}
                selectRange={false}
                {...{ locale: 'en-GB' }}
              />
            </div>
          </Card>
        </div>
        <div className={styles.content}>
          {
            isLoading &&
            <Card
              className={styles.loadingCard}
            >
              <SkeletonLoader />
            </Card>
          }
          {
            (!isLoading && claims.length === 0) ?
              <div className={styles.noRecords}>
                <EmptyRecord message="No Claims Found" />
              </div>
              :
              (!isLoading && claims.length > 0) &&
              <Table className="">
                <thead>
                  <tr>
                    <th>Claim Ref</th>
                    <th>Order ID</th>
                    <th>Claim Date</th>
                    <th>Claim Lines</th>
                    <th>Approved Lines</th>
                    <th>Status</th>
                    <th>Days Elapsed</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {
                    claims.map((claim: IClaim) => {
                      return (
                        <tr key={claim.reference}>
                          <td>{claim.reference}</td>
                          <td>{claim.orderNumber}</td>
                          <td>{formatShortDateString(claim.createdAt)}</td>
                          <td>{claim.lines.length}</td>
                          <td>{claim.approvedLines}</td>
                          <td>{addSpaceBetweenWords(ClaimStatus[claim.status])}</td>
                          <td>{claim.daysElapsed}</td>
                          <td>
                            <button
                              className="Table-iconAction"
                              onClick={() => navigateTo(`/orders/my-claims/${claim.orderNumber}`)}
                            >
                              <span className="icon-text-file" /><span className="underline">View Claim</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </Table>
          }
          {
            (totalPages > 1 && !isLoading) &&
            <Pagination className={styles.tablePagination}>
              <Button
                className="tertiary"
                type="submit"
                text="Previous"
                onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
              />
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <Button
                className="tertiary"
                type="submit"
                text="Next"
                onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          }
        </div>
      </div>
    </FullPageLayout>
  );
};

const mapStateToProps = (state: IStoreState) => {
  return {
    userStores: state.userStores.data,
    persistedMyClaimsListFilters: state.persistMyClaimsFilters,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    persistMyClaimsFilters: (params: IPersistedMyClaimsFilters) =>
      dispatch(persistMyClaimsFiltersAction(params)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(MyClaims);
